import React, { useState, useMemo } from 'react';
import { collection, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
// NOTE: The <Icon> component has been replaced with inline SVGs for reliability.
import ViewQuestionModal from '../../components/ViewQuestionModal';

const ManageQuestionsTab = ({ questions, employees, setNotification }) => {
    const [newQuestionText, setNewQuestionText] = useState('');
    const [assignedEmployee, setAssignedEmployee] = useState('');
    const [dueDate, setDueDate] = useState('');
    const [tags, setTags] = useState('');

    const [filterStatus, setFilterStatus] = useState('all');
    const [filterEmployee, setFilterEmployee] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');

    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [viewingQuestion, setViewingQuestion] = useState(null);

    const handleCreateQuestion = async (e) => {
        e.preventDefault();
        if (!newQuestionText || !assignedEmployee) {
            setNotification({ type: 'error', message: 'Question text and assigned employee are required.' });
            return;
        }
        try {
            await addDoc(collection(db, 'questions'), {
                text: newQuestionText,
                assignedTo: assignedEmployee,
                status: 'unanswered',
                marks: 0, // Default value
                dueDate: dueDate || null,
                tags: tags.split(',').map(tag => tag.trim()).filter(Boolean),
                createdAt: new Date(),
            });
            setNewQuestionText('');
            setAssignedEmployee('');
            setDueDate('');
            setTags('');
            setNotification({ type: 'success', message: 'Question created successfully!' });
        } catch (error) {
            setNotification({ type: 'error', message: `Error creating question: ${error.message}` });
        }
    };

    const filteredQuestions = useMemo(() => {
        return (questions || [])
            .filter(q => filterStatus === 'all' || q.status === filterStatus)
            .filter(q => filterEmployee === 'all' || q.assignedTo === filterEmployee)
            .filter(q => q.text.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [questions, filterStatus, filterEmployee, searchTerm]);

    const handleViewClick = (question) => {
        const employee = employees.find(emp => emp.id === question.assignedTo);
        setViewingQuestion({ ...question, assignedToName: employee ? employee.name : 'Unknown' });
        setIsViewModalOpen(true);
    };

    const handleDeleteClick = async (questionId) => {
        // A simple browser confirmation
        if (window.confirm("Are you sure you want to delete this question permanently? This action cannot be undone.")) {
            try {
                await deleteDoc(doc(db, "questions", questionId));
                setNotification({ type: 'success', message: 'Question deleted successfully.' });
            } catch (error) {
                console.error("Error deleting question: ", error);
                setNotification({ type: 'error', message: `Failed to delete question: ${error.message}` });
            }
        }
    };

    return (
        <div>
            {/* Create Question Form */}
            <div className="bg-white p-6 rounded-xl shadow-sm mb-8">
                <h3 className="text-xl font-bold text-slate-800 mb-4">Create New Question</h3>
                <form onSubmit={handleCreateQuestion} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                        <label htmlFor="questionText" className="block text-sm font-medium text-slate-700">Question Text</label>
                        <textarea id="questionText" value={newQuestionText} onChange={(e) => setNewQuestionText(e.target.value)} className="mt-1 block w-full border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" rows="3" required></textarea>
                    </div>
                    <div>
                        <label htmlFor="assignEmployee" className="block text-sm font-medium text-slate-700">Assign To</label>
                        <select id="assignEmployee" value={assignedEmployee} onChange={(e) => setAssignedEmployee(e.target.value)} className="mt-1 block w-full border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" required>
                            <option value="" disabled>Select Employee</option>
                            {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="dueDate" className="block text-sm font-medium text-slate-700">Due Date (Optional)</label>
                        <input type="date" id="dueDate" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1 block w-full border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                    <div className="md:col-span-2">
                        <label htmlFor="tags" className="block text-sm font-medium text-slate-700">Tags (comma-separated)</label>
                        <input type="text" id="tags" value={tags} onChange={(e) => setTags(e.target.value)} className="mt-1 block w-full border-slate-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" placeholder="e.g., Anatomy, Client Relations" />
                    </div>
                    <div className="md:col-span-2 text-right">
                        <button type="submit" className="font-semibold py-2 px-4 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white transition-colors shadow-sm">Create Question</button>
                    </div>
                </form>
            </div>
            
            {/* Filter Controls & Questions Table */}
            <div className="bg-white p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-bold text-slate-800 mb-4">Manage Questions</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <input type="text" placeholder="Search questions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="border-slate-300 rounded-md shadow-sm" />
                    <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="border-slate-300 rounded-md shadow-sm">
                        <option value="all">All Statuses</option>
                        <option value="unanswered">Unanswered</option>
                        <option value="in-review">In Review</option>
                        <option value="needs-revision">Needs Revision</option>
                        <option value="passed">Passed</option>
                    </select>
                    <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)} className="border-slate-300 rounded-md shadow-sm">
                        <option value="all">All Employees</option>
                        {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                    </select>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200">
                        <thead className="bg-slate-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Question</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Assigned To</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Due Date</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-slate-200">
                            {filteredQuestions.map(q => {
                                const employee = employees.find(e => e.id === q.assignedTo);
                                return (
                                    <tr key={q.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-800 max-w-sm truncate">{q.text}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{employee ? employee.name : 'Unknown'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 capitalize">{q.status.replace('-', ' ')}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{q.dueDate ? new Date(q.dueDate).toLocaleDateString() : 'N/A'}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm flex items-center gap-4">
                                            <button onClick={() => handleViewClick(q)} className="text-indigo-600 hover:text-indigo-900 transition-colors" title="View Details">
                                                {/* Eye Icon SVG */}
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                            </button>
                                            <button onClick={() => handleDeleteClick(q.id)} className="text-red-600 hover:text-red-900 transition-colors" title="Delete Question">
                                                {/* Trash2 Icon SVG */}
                                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5"><path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" x2="10" y1="11" y2="17"></line><line x1="14" x2="14" y1="11" y2="17"></line></svg>
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {isViewModalOpen && (
                <ViewQuestionModal
                    question={viewingQuestion}
                    onClose={() => setIsViewModalOpen(false)}
                />
            )}
        </div>
    );
};

export default ManageQuestionsTab;